'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  UserPlus, Check, Globe, Building2, ShoppingCart, PenLine, Brush,
  LayoutTemplate, ShoppingBag, Briefcase, UtensilsCrossed, Zap, Palette,
} from 'lucide-react'

interface Plan {
  id: number | string
  name: string
  slug: string
  price: number
}

const TEMPLATES = [
  {
    key: 'blank',
    label: 'Blank',
    description: 'Start from scratch',
    icon: LayoutTemplate,
    color: 'text-muted-foreground',
    bg: 'bg-muted/60',
    siteType: null,
    pages: null,
  },
  {
    key: 'store',
    label: 'Store',
    description: 'E-commerce · 5 pages',
    icon: ShoppingBag,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    siteType: 'store',
    pages: 5,
  },
  {
    key: 'agency',
    label: 'Agency',
    description: 'Business · 5 pages',
    icon: Briefcase,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    siteType: 'business',
    pages: 5,
  },
  {
    key: 'restaurant',
    label: 'Restaurant',
    description: 'Hospitality · 4 pages',
    icon: UtensilsCrossed,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    siteType: 'business',
    pages: 4,
  },
  {
    key: 'saas',
    label: 'SaaS',
    description: 'Software · 5 pages',
    icon: Zap,
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    siteType: 'business',
    pages: 5,
  },
  {
    key: 'portfolio',
    label: 'Portfolio',
    description: 'Creative · 3 pages',
    icon: Palette,
    color: 'text-[hsl(var(--primary))]',
    bg: 'bg-primary/5',
    siteType: 'portfolio',
    pages: 3,
  },
]

const SITE_TYPES = [
  { value: 'business', label: 'Business', icon: Building2 },
  { value: 'store', label: 'Store', icon: ShoppingCart },
  { value: 'blog', label: 'Blog', icon: PenLine },
  { value: 'portfolio', label: 'Portfolio', icon: Brush },
]

export default function SignUpPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [siteName, setSiteName] = useState('')
  const [siteType, setSiteType] = useState('business')
  const [template, setTemplate] = useState('blank')
  const [selectedPlan, setSelectedPlan] = useState('starter')
  const [plans, setPlans] = useState<Plan[]>([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const planParam = params.get('plan')
    if (planParam) setSelectedPlan(planParam)
  }, [])

  useEffect(() => {
    fetch('/api/plans?where[isActive][equals]=true&sort=sortOrder&limit=10')
      .then((res) => res.json())
      .then((data) => { if (data.docs) setPlans(data.docs) })
      .catch(() => {})
  }, [])

  function selectTemplate(key: string) {
    setTemplate(key)
    const tmpl = TEMPLATES.find((t) => t.key === key)
    if (tmpl?.siteType) setSiteType(tmpl.siteType)
  }

  const siteSlugPreview = siteName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) { setError('Passwords do not match'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return }
    if (!siteName.trim()) { setError('Please enter a site name'); return }
    if (siteSlugPreview.length < 2) { setError('Site name is too short'); return }

    setLoading(true)
    try {
      const res = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          password,
          siteName: siteName.trim(),
          siteType,
          planSlug: selectedPlan,
          template,
        }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Registration failed. Please try again.'); return }
      window.location.href = '/'
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const selectedTmpl = TEMPLATES.find((t) => t.key === template)

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Create Your Site</CardTitle>
          <CardDescription>Set up your account and website in one step</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
            )}

            {/* Plan Selection */}
            {plans.length > 0 && (
              <div className="space-y-2">
                <Label>Plan</Label>
                <div className="grid grid-cols-3 gap-2">
                  {plans.map((plan) => {
                    const isFree = plan.price === 0
                    const isSelected = selectedPlan === plan.slug
                    const isPaid = plan.price > 0
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        disabled={isPaid}
                        onClick={() => !isPaid && setSelectedPlan(plan.slug)}
                        className={cn(
                          'relative flex flex-col items-center p-3 rounded-lg border text-center transition-all',
                          isSelected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-border hover:border-primary/40',
                          isPaid && 'opacity-50 cursor-not-allowed',
                        )}
                      >
                        <span className="text-sm font-semibold">{plan.name}</span>
                        <span className="text-xs text-muted-foreground mt-1">
                          {isFree ? 'Free' : `$${(plan.price / 100).toFixed(0)}/mo`}
                        </span>
                        {isPaid && (
                          <Badge variant="outline" className="mt-1 text-[10px] px-1">Coming Soon</Badge>
                        )}
                        {isSelected && (
                          <Check className="absolute top-1.5 right-1.5 h-3.5 w-3.5 text-primary" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Account Info */}
            <div className="space-y-2">
              <Label htmlFor="name">Your Name</Label>
              <Input
                id="name" type="text" placeholder="John Doe" value={name}
                onChange={(e) => setName(e.target.value)} required autoComplete="name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" placeholder="you@example.com" value={email}
                onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password" type="password" placeholder="Min. 6 chars" value={password}
                  onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm</Label>
                <Input
                  id="confirmPassword" type="password" placeholder="Repeat" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password"
                />
              </div>
            </div>

            {/* Site Info */}
            <div className="border-t border-border/60 pt-5 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="siteName">Site Name</Label>
                <Input
                  id="siteName" type="text" placeholder="My Awesome Site" value={siteName}
                  onChange={(e) => setSiteName(e.target.value)} required
                />
                {siteSlugPreview && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Globe className="h-3 w-3" />
                    <span>
                      Your site will be at{' '}
                      <strong className="text-foreground">{siteSlugPreview}.stuys.com</strong>
                    </span>
                  </div>
                )}
              </div>

              {/* Template Picker */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Starting Template</Label>
                  {selectedTmpl && selectedTmpl.key !== 'blank' && (
                    <span className="text-xs text-muted-foreground">
                      {selectedTmpl.pages} pages included
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {TEMPLATES.map((tmpl) => {
                    const Icon = tmpl.icon
                    const isSelected = template === tmpl.key
                    return (
                      <button
                        key={tmpl.key}
                        type="button"
                        onClick={() => selectTemplate(tmpl.key)}
                        className={cn(
                          'relative flex flex-col items-center gap-1.5 p-3 rounded-lg border text-center transition-all',
                          isSelected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-border hover:border-primary/40',
                        )}
                      >
                        <div className={cn('p-1.5 rounded-lg', tmpl.bg)}>
                          <Icon className={cn('h-4 w-4', tmpl.color)} />
                        </div>
                        <span className="text-xs font-semibold leading-tight">{tmpl.label}</span>
                        <span className="text-[10px] text-muted-foreground leading-tight">
                          {tmpl.description}
                        </span>
                        {isSelected && (
                          <Check className="absolute top-1.5 right-1.5 h-3 w-3 text-primary" />
                        )}
                      </button>
                    )
                  })}
                </div>
                {template !== 'blank' && (
                  <p className="text-xs text-muted-foreground">
                    Your site will be pre-built with professional content. You can edit everything after signup.
                  </p>
                )}
              </div>

              {/* Site Type — only shown when Blank is selected */}
              {template === 'blank' && (
                <div className="space-y-2">
                  <Label>Site Type</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {SITE_TYPES.map((type) => {
                      const Icon = type.icon
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setSiteType(type.value)}
                          className={cn(
                            'flex flex-col items-center p-2.5 rounded-lg border text-center transition-all',
                            siteType === type.value
                              ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                              : 'border-border hover:border-primary/40',
                          )}
                        >
                          <Icon className="h-5 w-5 text-muted-foreground" />
                          <span className="text-xs font-medium mt-1">{type.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              <UserPlus className="mr-2 h-4 w-4" />
              {loading
                ? template !== 'blank'
                  ? 'Building your site…'
                  : 'Creating your site…'
                : 'Create My Site'}
            </Button>

            <p className="text-center text-sm text-muted-foreground mt-4">
              Already have an account?{' '}
              <a href="/signin" className="text-primary font-medium hover:underline">Sign in</a>
            </p>
          </form>
        </CardContent>
      </Card>
    </main>
  )
}
